import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OpportunityDialog } from "@/components/OpportunityDialog";
import { FollowUpBadge } from "@/components/FollowUpBadge";
import { Target, Plus } from "lucide-react";

const usePipeline = () => {
  const [p, setP] = useState({ stages: [], statuses: [] });
  useEffect(() => {
    Promise.all([api.get("/pipeline/stages"), api.get("/opp-statuses")])
      .then(([s, st]) => setP({ stages: s.data, statuses: st.data })).catch(() => {});
  }, []);
  return p;
};

export const ChatOpportunities = ({ chat, compact = false }) => {
  const { stages, statuses } = usePipeline();
  const [opps, setOpps] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/opportunities", { params: { phone: chat.phone } }).then(({ data }) => setOpps(data)).catch(() => {});
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("opps-changed", h);
    return () => window.removeEventListener("opps-changed", h);
  }, [chat.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => window.dispatchEvent(new Event("opps-changed"));

  const prefill = { title: `Oportunidade · ${chat.contact_name}`, contact_name: chat.contact_name, contact_phone: chat.phone, chat_id: chat.id };
  const dialog = <OpportunityDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} initial={editing} stages={stages} statuses={statuses} onSaved={changed} />;

  if (compact) {
    return (
      <>
        <Button size="sm" variant="outline" className="px-2 sm:px-3" onClick={() => setEditing(prefill)} data-testid="chat-opportunity-button" title="Criar oportunidade no CRM">
          <Target className="w-4 h-4 xl:mr-1" /> <span className="hidden xl:inline">Oportunidade</span>
          {opps.length > 0 && <span className="ml-1 text-[10px] font-bold text-emerald-700">{opps.length}</span>}
        </Button>
        {dialog}
      </>
    );
  }
  return (
    <div data-testid="chat-opportunities">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <Target className="w-3 h-3" /> Oportunidades
        <button onClick={() => setEditing(prefill)} className="ml-auto text-emerald-700 hover:text-emerald-800 normal-case flex items-center gap-0.5" data-testid="chat-opportunity-add">
          <Plus className="w-3 h-3" /> Nova
        </button>
      </div>
      {opps.length === 0 && <div className="text-xs text-muted-foreground">Nenhuma oportunidade para este contato.</div>}
      <div className="space-y-1.5">
        {opps.map((o) => {
          const st = statuses.find((s) => s.id === o.status_id);
          const stage = stages.find((s) => s.id === o.stage);
          return (
            <button key={o.id} onClick={() => setEditing(o)} className="w-full text-left p-2 rounded-lg border border-border hover:bg-muted transition-colors" data-testid="chat-opportunity-item">
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-border" style={{ background: st?.color || "transparent" }} />
                <span className="truncate">{o.title}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {stage && <span className="text-[10px] px-1.5 py-0.5 rounded text-white" style={{ background: stage.color }}>{stage.name}</span>}
                <FollowUpBadge iso={o.follow_up_at} />
              </div>
            </button>
          );
        })}
      </div>
      {dialog}
    </div>
  );
};
